import React, { useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { Card, CardContent } from "../ui/card";
import { TradeEntry, DailyPerformance } from "../../types/tradingJournal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "../ui/badge";

interface CalendarViewProps {
  entries: TradeEntry[];
  selectedMonth: Date;
  onMonthChange: (date: Date) => void;
}

const CalendarView = ({
  entries,
  selectedMonth,
  onMonthChange,
}: CalendarViewProps) => {
  // Group entries by date
  const dailyPerformance = useMemo(() => {
    const performanceMap = new Map<string, DailyPerformance>();

    entries.forEach((entry) => {
      const dateStr = entry.date;
      const existing = performanceMap.get(dateStr);

      if (existing) {
        existing.netPnl += entry.pnl;
        existing.trades.push(entry);
      } else {
        performanceMap.set(dateStr, {
          date: dateStr,
          netPnl: entry.pnl,
          trades: [entry],
        });
      }
    });

    return Array.from(performanceMap.values());
  }, [entries]);

  // Generate calendar days for the selected month
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [selectedMonth]);

  // Find performance for a specific day
  const getDayPerformance = (day: Date) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return dailyPerformance.find((p) => p.date === dateStr);
  };

  // Navigate between months
  const previousMonth = () => {
    const prevMonth = new Date(selectedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    onMonthChange(prevMonth);
  };

  const nextMonth = () => {
    const nextMonth = new Date(selectedMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    onMonthChange(nextMonth);
  };

  // Generate day cell background color based on P&L
  const getDayColor = (performance?: DailyPerformance) => {
    if (!performance) return "bg-white/5";

    const pnl = performance.netPnl;
    if (pnl > 0) {
      // Green gradient based on profit amount
      const intensity = Math.min(0.5 + (pnl / 10000) * 0.5, 0.9);
      return `bg-green-900/${intensity}`;
    } else if (pnl < 0) {
      // Red gradient based on loss amount
      const intensity = Math.min(0.5 + (Math.abs(pnl) / 10000) * 0.5, 0.9);
      return `bg-red-900/${intensity}`;
    }
    return "bg-white/5";
  };

  return (
    <Card className="intellectia-card">
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium">
            {format(selectedMonth, "MMMM yyyy")}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={previousMonth}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextMonth}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 text-center mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="py-2 text-sm font-medium text-white/70">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, i) => {
            const performance = getDayPerformance(day);
            return (
              <TooltipProvider key={i}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`
                        aspect-square rounded-lg flex flex-col items-center justify-center
                        ${getDayColor(
                          performance
                        )} border border-white/5 transition-colors
                        ${
                          performance
                            ? "cursor-pointer hover:border-white/20"
                            : "cursor-default"
                        }
                      `}
                    >
                      <div className="text-sm">{format(day, "d")}</div>
                      {performance && (
                        <div
                          className={`text-xs font-medium ${
                            performance.netPnl >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          ₹{Math.abs(performance.netPnl).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </TooltipTrigger>
                  {performance && (
                    <TooltipContent className="max-w-md" side="top">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {format(day, "MMM d, yyyy")}
                          </span>
                          <span
                            className={`font-medium ${
                              performance.netPnl >= 0
                                ? "text-green-400"
                                : "text-red-400"
                            }`}
                          >
                            ₹{performance.netPnl.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm space-y-2">
                          {performance.trades.map((trade, idx) => (
                            <div
                              key={idx}
                              className="border-t border-white/10 pt-2 first:border-0 first:pt-0"
                            >
                              <div className="flex justify-between">
                                <span>{trade.stock}</span>
                                <span
                                  className={
                                    trade.pnl >= 0
                                      ? "text-green-400"
                                      : "text-red-400"
                                  }
                                >
                                  ₹{trade.pnl.toLocaleString()} (
                                  {trade.roi.toFixed(2)}%)
                                </span>
                              </div>
                              {trade.setup && (
                                <Badge
                                  variant="outline"
                                  className="mt-1 mr-1 bg-white/10"
                                >
                                  {trade.setup}
                                </Badge>
                              )}
                              {trade.mistakes &&
                                trade.mistakes.length > 0 &&
                                trade.mistakes.map((mistake, midx) => (
                                  <Badge
                                    key={midx}
                                    variant="outline"
                                    className="mt-1 mr-1 bg-red-900/30 text-red-400 border-red-500/20"
                                  >
                                    {mistake}
                                  </Badge>
                                ))}
                              {trade.reason && (
                                <div className="text-xs text-white/70 mt-1 truncate max-w-60">
                                  {trade.reason}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarView;
